<template>
  <ListComponent title="Inbound Integration Logs">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchLogs"
            class="mr-2"
          ></v-text-field>
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFiltersDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters class="ma-0">
              <v-col cols="12" class="font-weight-bold">
                Applied Filters
              </v-col>

              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col cols="4" class="px-0 pt-0 pb-2">
          <v-tabs height="35" v-model="tab" centered>
            <v-tabs-slider></v-tabs-slider>
            <v-tab> Company </v-tab>
            <v-tab> Driver </v-tab>
            <v-tab> Driver Leave </v-tab>
            <v-tab> Asset Maintenance </v-tab>
          </v-tabs>
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="4"
          class="px-0 pt-0 pb-2 d-flex justify-end"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

      <v-col cols="12" class="pa-0">
        <AgGridVue
          style="width: 100%; height: calc(100vh - 172px)"
          class="ag-theme-alpine"
          id="myGrid"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          @columnMoved="resizeGrid"
          @columnVisible="resizeGrid"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="apiLogsList"
        ></AgGridVue>
      </v-col>
    </template>
    <template #dialogs>
      <InboundIntegrationLogFilters
        :bindingObject="filters"
        v-model="showFiltersDialog"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(15)"
      />
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import IntegrationLogButton from "@/components/AgGridComponents/IntegrationLogButton.vue";
import InboundIntegrationLogFilters from "@/components/ModuleBased/dialogs/filters/InboundIntegrationLogFilters.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    agColumnHeader: CustomHeader,
    DetailsParent,
    IntegrationLogButton,
    InboundIntegrationLogFilters,
  },
  watch: {
    tab() {
      this.pageNo = 1;
      this.apiCall();
    },
  },
  data() {
    return {
      sorting: {},
      showFiltersDialog: false,
      headers: [
        {
          headerName: "Created On",
          field: "created",
          tooltipField: "created",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value, true);
          },
        },
        {
          headerName: "End Point",
          field: "endpoint",
          tooltipField: "endpoint",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Response Code",
          field: "response_code",
          tooltipField: "response_code",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Method",
          field: "method",
          tooltipField: "method",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Detail",
          field: "detail",
          tooltipField: "detail",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Status",
          field: "status",
          tooltipField: "last_name",
          minWidth: 120,
          cellRenderer: function (params) {
            const result = /^2/.test(params.data.response_code.toString())
              ? `<span class="success-border px-2 py-1 success--text"> Success </span>`
              : `<span class="error-border px-2 py-1 error--text"> Failed </span>`;
            return result;
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "IntegrationLogButton",
          pinned: "right",
          width: 70,
        },
      ],
      apiLogsList: [],
      // pagination vars
      itemsPerPage: 20,
      pageNo: 1,
      totalItems: 0,

      // Filter vars
      filters: {},

      tab: null,
      permissions: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },

    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    apiCall() {
      if (this.tab === 0) {
        this.getCompanyIntegrationLogs();
      } else if (this.tab === 1) {
        this.getDriverIntegrationLogs();
      } else if (this.tab === 2) {
        this.getDriverLeaveIntegrationLogs();
      } else {
        this.getAssetMaintenanceIntegrationLogs();
      }
    },
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchLogs() {
      this.pageNo = 1;
      this.apiCall();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.apiCall();
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(15);
    },
    prevPage() {
      this.pageNo--;
      this.apiCall();
    },
    nextPage() {
      this.pageNo++;
      this.apiCall();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },

    getCompanyIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getCompanyIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getDriverIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getDriverIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getDriverLeaveIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getDriverLeaveIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getAssetMaintenanceIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getAssetMaintenanceIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getIntegrationResponseBodyBlob(id) {
      this.$bus.$emit("showLoader", true);

      this.$api.integration
        .getIntegrationResponseBodyBlob(id)
        .then((resp) => {
          let jsonString = JSON.parse(resp.data);
          let blob = new Blob([jsonString], {
            type: "application/json",
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "inboundintegration" + ".json";
          link.click();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("integration");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.apiCall();
      }
    }
  },
};
</script>

<style>
.success-border {
  border: 1px solid #4caf50;
  border-radius: 30px;
}

.error-border {
  border: 1px solid #ff5252;
  border-radius: 30px;
}
</style>
